import React, { useCallback } from "react";
import { useParams } from "react-router-dom";
import { AccountType, AreaOfOperation, BankingStructure, EntityType, IndustryDivision } from "../../../../../api/fmr1";
import { useAppDispatch, useAppSelector } from "../../../../../app/store";
import { DraftFMR1Fields, selectFMR1DraftFmrFields, updateFMR1DraftField } from "../../../logic/frm1Slice";
import FmrTwoColumnTable, { FmrTwoColumnTableRowProps } from "../common/fmrTwoColumnTable";

export const PART1_TABLE1_ROWS: Array<FmrTwoColumnTableRowProps<DraftFMR1Fields>> = [
  {
    label: "Table 1 : Title of the Field (**Mandatory Field)",
    rightSectionLabel: "Values",
    kind: "section",
  },
  {
    label: "1 Choose the type of Entity which is reporting",
    key: "fraud_entity_type",
    type: "dropdown",
    options: [
      {
        name: "Commercial banks, AIFIs",
        value: EntityType.COMMERCIAL_BANKS_AIFIS,
      },
      {
        name: "Urban Cooperative Banks",
        value: EntityType.URBAN_COOPERATIVE_BANKS,
      },
      {
        name: "Non-Banking Financial Companies",
        value: EntityType.NON_BANKING_FINANCIAL_COMPANIES,
      },
    ],
  },
  { label: "1 (a) Code of the entity", key: "fraud_entity_code", type: "text" },
  {
    label: "1 (b) Name of the Bank/AIFI/NBFC",
    key: "fraud_entity_bank_name",
    type: "text",
  },
  { label: "2 Fraud Number", key: "fraud_number", type: "text" },

  { label: "3 Details of the branch**", kind: "section" },
  { label: "3.a Branch Code**", key: "branch_code", type: "text" },
  { label: "3.b Name of the branch**", key: "branch_name", type: "text" },
  {
    label: "3.c Other Code",
    key: "branch_other_code",
    type: "text",
    disabled: true,
  },

  {
    label: "4 Status of Perpetrator/s",
    key: "fraud_perpetrator_status",
    type: "dropdown",
    options: [
      { name: "Known", value: "Known" },
      { name: "Unknown", value: "Unknown" },
    ],
  },

  {
    label: "4.1 Type of Account where the fraud was perpetrated",
    key: "fraud_perpetrator_account_type",
    type: "dropdown",
    options: [
      { name: "Corporate", value: AccountType.CORPORATE },
      { name: "Individuals/HUF", value: AccountType.INDIVIDUALS_HUF },
      { name: "Sole proprietorship", value: AccountType.SOLE_PROPRIETORSHIP },
      { name: "Partnership firm", value: AccountType.PARTNERSHIP_FIRM },
      {
        name: "Limited Liability Partnership firm",
        value: AccountType.LIMITED_LIABILITY_PARTNERSHIP,
      },
      {
        name: "Society/non-profit organisations (including NGOs)",
        value: AccountType.SOCIETY_NON_PROFIT,
      },
      { name: "Trust", value: AccountType.TRUST },
      { name: "Others (Please specify)", value: AccountType.OTHERS },
    ],
  },
  {
    label: "4.1.1 Others (Please specify)",
    key: "fraud_perpetrator_account_type_other",
    type: "text",
  },
  {
    label: "4.2 Industry/Sector of the Account where fraud was perpetrated**",
    key: "fraud_perpetrator_industry_sector",
    type: "dropdown",
    options: Object.values(IndustryDivision).map((value) => ({
      name: value,
      value: value,
    })),
  },
  {
    label: "5.a Area of operation where fraud has occurred",
    key: "lender_details_area_of_operation",
    type: "dropdown",
    options: Object.values(AreaOfOperation).map((value) => ({
      name: value,
      value,
    }))
  },
  {
    label: "5.a.1 Others",
    key: "lender_details_area_of_operation_other",
    type: "text",
  },
  {
    label: "5.b Whether fraud has occurred in a borrowal account ? **",
    key: "lender_details_fraud_in_borrowal_account",
    type: "dropdown",
    options: [
      { name: "Yes", value: "Yes" },
      { name: "No", value: "No" },
    ],
  },
  {
    label: "5.c Whether the bank is part of the Consortium, Multiple Banking Arrangement (MBA), Sole banking? **",
    key: "lender_details_banking_structure",
    type: "dropdown",
    options: [
      { name: "Consortium", value: BankingStructure.CONSORTIUM },
      {
        name: "Multiple Banking Arrangement (MBA)",
        value: BankingStructure.MULTIPLE_BANKING_ARRANGEMENT,
      },
      { name: "Sole banking", value: BankingStructure.SOLE_BANKING },
    ],
  },
  {
    label: "5.d Name of consortium lead bank/Largest lender under MBA **",
    key: "lender_details_consortium_lead_bank",
    type: "text",
  },
  {
    label: "5.d.1 Others (Please Specify)",
    key: "lender_details_consortium_lead_other",
    type: "text",
  },
  {
    label: "5.e Consortium Leader/Largest lender under MBA Amount **",
    key: "lender_details_consortium_leader_amount",
    type: "number",
  },
  {
    label: "Table 2: FMR - Consortium table",
    kind: "section",
  },
  //TODO:Clarify no corresponding field name
  //TODO: Add consortium_advance
  //  consortium_advance: Optional[float] = Field(None, alias="Consortium Advance")
];

export interface FMR1Table1FormProps {
  leftWidthPx?: number;
}

const FMR1Table1Form: React.FC<FMR1Table1FormProps> = ({ leftWidthPx }) => {
  const { id: case_id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const draftFields = useAppSelector(selectFMR1DraftFmrFields);

  const handleFieldChange = useCallback((key: keyof DraftFMR1Fields, value: any) => {
    if (!case_id) return;
    dispatch(updateFMR1DraftField({ key: key as keyof DraftFMR1Fields, value }));
  }, [case_id, dispatch]);

  return (
    <>
      <FmrTwoColumnTable<DraftFMR1Fields>
        data={draftFields}
        onChange={handleFieldChange}
        rows={PART1_TABLE1_ROWS}
        leftWidthPx={leftWidthPx ?? 435}
        rowHeightPx={44}
        className="overflow-visible"
      />
    </>
  );
};

FMR1Table1Form.displayName = "FMR1Table1Form";

export default FMR1Table1Form;
